import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { bankUploadApi, apiErrorMessage } from '../services/api';
import { needsReviewCount } from '../utils/apiError';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { Button, Card, PageSkeleton, ErrorState, EmptyState, ConfirmDialog, Spinner } from '../components/ui';
import MathText from '../components/MathText';
import {
  getQuestionText,
  getQuestionImageUrl,
  getSourceImageUrl,
  getQuestionOptions,
  getCorrectOptionIds,
  getOptionExplanation,
  getQuestionExplanation,
  getQuestionTags
} from '../utils/question';
import { answerCorrection, jobErrorMessage, mergeReviewedQuestion, reviewQuestionId, sortReviewQuestions } from '../utils/review';

export default function QuestionBankUploadReviewPage() {
  const { jobId } = useParams();
  const org = useOrg();
  const toast = useToast();

  const [job, setJob] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState('all'); // 'all' | 'needs_review' | 'excluded' | 'active'
  const [search, setSearch] = useState('');
  const [updatingQuestionId, setUpdatingQuestionId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [unreviewedCount, setUnreviewedCount] = useState(null); // set when publish hits the review gate
  const [editingExplanation, setEditingExplanation] = useState(null); // { questionId, text }
  const [originalShown, setOriginalShown] = useState({}); // questionId -> showing the paper's own crop
  const [retrying, setRetrying] = useState(false);

  const loadData = useCallback(async () => {
    if (!org?.id || !jobId) return;
    setLoading(true);
    setError(null);
    try {
      const [jobRes, questionsRes] = await Promise.all([
        bankUploadApi.getJob(org.id, jobId),
        bankUploadApi.getJobQuestions(org.id, jobId)
      ]);
      const jobData = jobRes.data?.job || jobRes.data;
      const qList = Array.isArray(questionsRes.data)
        ? questionsRes.data
        : Array.isArray(questionsRes.data?.questions)
          ? questionsRes.data.questions
          : Array.isArray(questionsRes.data?.items)
            ? questionsRes.data.items
            : [];
      setJob(jobData);
      // Paper order (page, then position); the API already sorts, this keeps it so.
      setQuestions(sortReviewQuestions(qList));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load question bank review'));
    } finally {
      setLoading(false);
    }
  }, [org?.id, jobId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Send one review edit and replace the local copy with what the API stored.
  const saveReview = async (question, body, successMessage, failureMessage) => {
    const qId = reviewQuestionId(question);
    if (!qId || !org?.id || !jobId) return false;
    setUpdatingQuestionId(qId);
    try {
      const res = await bankUploadApi.updateJobQuestion(org.id, jobId, qId, body);
      setQuestions((prev) =>
        prev.map((q) => (reviewQuestionId(q) === qId ? mergeReviewedQuestion(q, res.data) : q))
      );
      toast.success(successMessage);
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, failureMessage));
      return false;
    } finally {
      setUpdatingQuestionId(null);
    }
  };

  // Handle toggling excluded status for a question
  const handleToggleExclude = (question) => {
    const nextExcluded = !question.excluded;
    return saveReview(
      question,
      { excluded: nextExcluded },
      nextExcluded ? 'Question excluded from publish.' : 'Question included for publish.',
      'Failed to update question status'
    );
  };

  // Mark an option correct. Saving any answer clears the question from Needs
  // Review, so confirming the option already marked correct is how an owner
  // signs off an AI-guessed answer that turned out right.
  const handleSetCorrectOption = (question, targetOptionId, { confirming = false } = {}) =>
    saveReview(
      question,
      { answer: answerCorrection(question, targetOptionId) },
      confirming ? 'Answer confirmed.' : 'Correct answer updated.',
      'Failed to update correct answer'
    );

  const handleSaveExplanation = async (question, optionId, text) => {
    const saved = await saveReview(
      question,
      { answer: answerCorrection(question, optionId, { explanation: text }) },
      'Explanation saved.',
      'Failed to save the explanation'
    );
    if (saved) setEditingExplanation(null);
  };

  // Handle publishing question bank version. The server refuses (409) while
  // AI-guessed answers are unreviewed; the owner may then publish anyway.
  const handlePublish = async ({ force = false } = {}) => {
    if (!org?.id || !jobId) return;
    setPublishing(true);
    try {
      await bankUploadApi.publish(org.id, jobId, { force });
      setJob((prev) => (prev ? { ...prev, reviewStatus: 'published' } : prev));
      // In local state, mark non-excluded questions as active
      setQuestions((prev) => prev.map((q) => ({ ...q, isActive: !q.excluded })));
      setUnreviewedCount(null);
      toast.success('Question bank published! Active questions are now live in quizzes and screeners.');
    } catch (err) {
      const unreviewed = force ? null : needsReviewCount(err);
      if (unreviewed) {
        setUnreviewedCount(unreviewed);
      } else {
        setUnreviewedCount(null);
        toast.error(apiErrorMessage(err, 'Failed to publish question bank'));
      }
    } finally {
      setPublishing(false);
    }
  };

  // Handle unpublishing question bank version
  const handleUnpublish = async () => {
    if (!org?.id || !jobId) return;
    setPublishing(true);
    try {
      await bankUploadApi.unpublish(org.id, jobId);
      setJob((prev) => (prev ? { ...prev, reviewStatus: 'draft' } : prev));
      // In local state, mark all questions as inactive (draft)
      setQuestions((prev) => prev.map((q) => ({ ...q, isActive: false })));
      toast.success('Question bank unpublished and set back to Draft.');
      setConfirmUnpublish(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to unpublish question bank'));
    } finally {
      setPublishing(false);
    }
  };

  const handleRetryJob = async () => {
    if (!jobId || retrying) return;
    setRetrying(true);
    try {
      await bankUploadApi.retryJob(org.id, jobId);
      toast.success('Extraction job restarted! PDF has been re-queued for processing.');
      await loadData();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to restart extraction job'));
    } finally {
      setRetrying(false);
    }
  };

  // Counts & filters
  const stats = useMemo(() => {
    const total = questions.length;
    const needsReview = questions.filter((q) => q.needsReview === true || q.answerProvided === false).length;
    const excluded = questions.filter((q) => q.excluded === true).length;
    const active = questions.filter((q) => q.isActive === true).length;
    const readyToPublish = questions.filter((q) => !q.excluded).length;
    return { total, needsReview, excluded, active, readyToPublish };
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    let list = questions;

    if (filter === 'needs_review') {
      list = list.filter((q) => q.needsReview === true || q.answerProvided === false);
    } else if (filter === 'excluded') {
      list = list.filter((q) => q.excluded === true);
    } else if (filter === 'active') {
      list = list.filter((q) => q.isActive === true);
    }

    const query = search.trim().toLowerCase();
    if (!query) return list;

    return list.filter((q) => {
      const text = getQuestionText(q).toLowerCase();
      const tags = getQuestionTags(q).join(' ').toLowerCase();
      const options = getQuestionOptions(q).map((o) => o.label).join(' ').toLowerCase();
      return text.includes(query) || tags.includes(query) || options.includes(query);
    });
  }, [questions, filter, search]);

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  const isPublished = job?.reviewStatus === 'published';
  const paperTitle = job?.paperTitle || job?.fileName || 'Question Paper';
  const examName = job?.examTitle || job?.examShortName || job?.examId || 'Enrolled Exam';
  const status = (job?.status || '').toUpperCase();
  const isFailed = status === 'ERROR' || status === 'FAILED' || status === 'UPLOAD_FAILED';

  return (
    <>
      <div className="exam-header">
        <div className="exam-header-top">
          <div className="breadcrumb">
            <Link to="/question-banks">Question Banks</Link>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-current">Review: {paperTitle}</span>
          </div>

          <div className="row-actions">
            {isFailed && (
              <Button
                variant="secondary"
                disabled={retrying}
                onClick={handleRetryJob}
              >
                {retrying ? <Spinner /> : 'Retry Extraction ↺'}
              </Button>
            )}
            {isPublished ? (
              <>
                <span className="status">✓ Published & Live</span>
                <Button
                  variant="secondary"
                  disabled={publishing}
                  onClick={() => setConfirmUnpublish(true)}
                >
                  Unpublish to Draft
                </Button>
              </>
            ) : (
              <>
                <span className="status draft">Draft Version</span>
                <Button
                  disabled={publishing || stats.readyToPublish === 0}
                  onClick={() => handlePublish()}
                >
                  {publishing ? 'Publishing…' : `Publish ${stats.readyToPublish} Questions`}
                </Button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>{paperTitle}</h1>
            <p className="muted" style={{ fontSize: 13 }}>
              Associated Exam: <b>{examName}</b>
              {job?.paperYear ? ` · Year: ${job.paperYear}` : ''}
              {job?.paperType ? ` · Type: ${job.paperType}` : ''}
              {job?.createdAt ? ` · Uploaded: ${new Date(job.createdAt).toLocaleDateString()}` : ''}
            </p>
            {isFailed && jobErrorMessage(job) && (
              <p className="danger" style={{ fontSize: 13, marginTop: 6 }}>{jobErrorMessage(job)}</p>
            )}
          </div>

          <div className="stat-chips">
            <span className="stat-chip">
              Status: <b>{status === 'COMPLETED_WITH_ERRORS' ? 'Completed with errors' : status}</b>
            </span>
            <span className="stat-chip">
              Channel: <b>Institute (B2B)</b>
            </span>
          </div>
        </div>

        <div className="metrics" style={{ marginTop: 16 }}>
          <Card className="metric">
            <p>Total Extracted</p>
            <h2>{stats.total}</h2>
            <small style={{ color: 'var(--blue)' }}>From {job?.pagesProcessed || job?.totalPages || 'PDF'} pages</small>
          </Card>
          <Card className="metric">
            <p>Needs Review</p>
            <h2 style={{ color: stats.needsReview > 0 ? 'var(--amber)' : 'inherit' }}>{stats.needsReview}</h2>
            <small style={{ color: stats.needsReview > 0 ? 'var(--amber)' : 'var(--green)' }}>
              {stats.needsReview > 0 ? 'Missing answer key / hierarchy' : 'All questions verified'}
            </small>
          </Card>
          <Card className="metric">
            <p>Excluded</p>
            <h2>{stats.excluded}</h2>
            <small style={{ color: 'var(--muted)' }}>Will not be published</small>
          </Card>
          <Card className="metric">
            <p>Ready to Publish</p>
            <h2 style={{ color: 'var(--green)' }}>{stats.readyToPublish}</h2>
            <small style={{ color: isPublished ? 'var(--green)' : 'var(--muted)' }}>
              {isPublished ? 'Currently active in quizzes' : 'Will activate upon publish'}
            </small>
          </Card>
        </div>
      </div>

      <div className="review-instruction-banner">
        <div style={{ flex: 1 }}>
          <b>📋 Review Workflow:</b> Verify that question stems and answer keys are accurate. Where the AI guessed an answer, click <em>Confirm answer</em> if it is right or <em>Set Correct</em> on the right option. Toggle <em>Exclude</em> on any unwanted question.
        </div>
        {!isPublished && (
          <Button onClick={() => handlePublish()} disabled={publishing || stats.readyToPublish === 0}>
            {publishing ? 'Publishing…' : 'Publish Bank'}
          </Button>
        )}
      </div>

      <div className="tabs" style={{ marginTop: 20 }}>
        <button
          className={filter === 'all' ? 'selected' : ''}
          onClick={() => setFilter('all')}
        >
          All Questions ({stats.total})
        </button>
        <button
          className={filter === 'needs_review' ? 'selected' : ''}
          onClick={() => setFilter('needs_review')}
        >
          ⚠️ Needs Review ({stats.needsReview})
        </button>
        <button
          className={filter === 'excluded' ? 'selected' : ''}
          onClick={() => setFilter('excluded')}
        >
          🚫 Excluded ({stats.excluded})
        </button>
        {isPublished && (
          <button
            className={filter === 'active' ? 'selected' : ''}
            onClick={() => setFilter('active')}
          >
            ✓ Active ({stats.active})
          </button>
        )}

        <span></span>

        <div className="bank-search">
          <input
            type="text"
            placeholder="Search questions or tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {!filteredQuestions.length ? (
        <EmptyState
          title={search ? 'No matching questions found' : 'No questions in this filter'}
          action={
            (filter !== 'all' || search) && (
              <Button variant="secondary" onClick={() => { setFilter('all'); setSearch(''); }}>
                View all questions
              </Button>
            )
          }
        >
          {search ? 'Try clearing or changing your search query.' : 'There are no questions in this category.'}
        </EmptyState>
      ) : (
        <div className="review-question-list">
          {filteredQuestions.map((question, index) => {
            const qId = reviewQuestionId(question) || index;
            const isUpdating = updatingQuestionId === qId;
            const options = getQuestionOptions(question);
            const correctIds = getCorrectOptionIds(question, options);
            // The explanation belongs to the one correct option; it is only editable when there is one.
            const correctOptionId = correctIds.size === 1 && reviewQuestionId(question) ? [...correctIds][0] : null;
            const isEditingExplanation = editingExplanation?.questionId === qId && correctOptionId != null;
            const explanation = getQuestionExplanation(question);
            const tags = getQuestionTags(question);
            const isExcluded = question.excluded === true;
            const needsReview = question.needsReview === true || question.answerProvided === false;
            const noAnswerKey = question.answerProvided === false;

            const displayImageUrl = getQuestionImageUrl(question);
            // The paper's own crop, when the shown diagram differs from it (usually an AI redraw).
            const sourceImageUrl = getSourceImageUrl(question);
            const hasOriginal = Boolean(sourceImageUrl && sourceImageUrl !== displayImageUrl);
            const showingOriginal = hasOriginal && (originalShown[qId] || !displayImageUrl);
            const imageUrl = showingOriginal ? sourceImageUrl : displayImageUrl;

            return (
              <Card
                key={qId}
                className={`review-question-card ${isExcluded ? 'excluded-card' : ''} ${needsReview ? 'needs-review-card' : ''}`}
              >
                <div className="review-question-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="question-row-num">#{index + 1}</span>
                    {question.sectionTitle && (
                      <span className="exam-pill">{question.sectionTitle}</span>
                    )}
                    {question.topicTitle && (
                      <span className="muted">Topic: {question.topicTitle}</span>
                    )}
                  </div>

                  <div className="review-question-badges">
                    {noAnswerKey && (
                      <span className="badge-warning" title="No answer key found on the page in PDF">
                        ⚠️ No answer key in PDF
                      </span>
                    )}
                    {needsReview && !noAnswerKey && (
                      <span className="badge-warning">
                        ⚠️ Needs Review
                      </span>
                    )}
                    {isExcluded && (
                      <span className="badge-danger">
                        Excluded
                      </span>
                    )}
                    {question.isActive && !isExcluded && (
                      <span className="badge-success">
                        Active in Bank
                      </span>
                    )}

                    <Button
                      variant={isExcluded ? 'secondary' : 'danger'}
                      className="exclude-action-btn"
                      disabled={isUpdating}
                      onClick={() => handleToggleExclude(question)}
                    >
                      {isUpdating ? 'Saving…' : isExcluded ? 'Restore' : 'Exclude'}
                    </Button>
                  </div>
                </div>

                <div className="review-question-body">
                  <p className="question-stem">
                    <MathText text={getQuestionText(question)} />
                  </p>

                  {imageUrl && (
                    <div className="question-image-wrap">
                      <img
                        src={imageUrl}
                        alt={showingOriginal ? 'Question diagram as printed in the paper' : 'Question diagram'}
                        className="question-image"
                        loading="lazy"
                      />
                      {hasOriginal && (
                        <div className="question-image-source">
                          <span className="muted">
                            {showingOriginal
                              ? 'Original from the paper'
                              : question.imageGenerated ? 'AI redraw of the diagram' : 'Processed diagram'}
                          </span>
                          {displayImageUrl && (
                            <button
                              type="button"
                              className="set-correct-btn"
                              aria-pressed={showingOriginal}
                              onClick={() => setOriginalShown((prev) => ({ ...prev, [qId]: !showingOriginal }))}
                            >
                              {showingOriginal ? (question.imageGenerated ? 'Show redraw' : 'Show processed') : 'Show original'}
                            </button>
                          )}
                          <a href={sourceImageUrl} target="_blank" rel="noopener noreferrer">Open original ↗</a>
                        </div>
                      )}
                    </div>
                  )}

                  {options.length > 0 && (
                    <div className="review-options-grid">
                      {options.map((opt, optIndex) => {
                        const isCorrect = correctIds.has(opt.id);
                        return (
                          <div
                            key={`${opt.id}-${optIndex}`}
                            className={`review-option-item ${isCorrect ? 'correct' : ''}`}
                          >
                            <span className="question-option-marker">
                              {isCorrect ? '✓' : String.fromCharCode(65 + optIndex)}
                            </span>
                            <div className="review-option-text">
                              {opt.label && <div><MathText text={opt.label} /></div>}
                              {opt.imageUrl && (
                                <img src={opt.imageUrl} alt={`Option ${String.fromCharCode(65 + optIndex)} diagram`} className="option-image" loading="lazy" />
                              )}
                            </div>

                            <div className="review-option-action">
                              {isCorrect ? (
                                needsReview && correctIds.size === 1 ? (
                                  <button
                                    type="button"
                                    className="set-correct-btn confirm"
                                    disabled={isUpdating}
                                    title="The AI picked this answer. Confirm it to clear the question from Needs Review."
                                    onClick={() => handleSetCorrectOption(question, opt.id, { confirming: true })}
                                  >
                                    Confirm answer
                                  </button>
                                ) : (
                                  <span className="question-option-badge">Correct Answer</span>
                                )
                              ) : (
                                <button
                                  type="button"
                                  className="set-correct-btn"
                                  disabled={isUpdating}
                                  onClick={() => handleSetCorrectOption(question, opt.id)}
                                >
                                  Set Correct
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {isEditingExplanation ? (
                    <div className="question-detail-section explanation-editor" style={{ marginTop: 12 }}>
                      <h4>Explanation</h4>
                      <textarea
                        rows={3}
                        value={editingExplanation.text}
                        onChange={(e) => setEditingExplanation({ questionId: qId, text: e.target.value })}
                        aria-label="Explanation"
                        placeholder="Why this option is correct"
                      />
                      <div className="row-actions">
                        <Button
                          disabled={isUpdating}
                          onClick={() => handleSaveExplanation(question, correctOptionId, editingExplanation.text)}
                        >
                          {isUpdating ? 'Saving…' : 'Save explanation'}
                        </Button>
                        <Button variant="secondary" disabled={isUpdating} onClick={() => setEditingExplanation(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (explanation || correctOptionId != null) && (
                    <div className="question-detail-section" style={{ marginTop: 12 }}>
                      <h4>Explanation</h4>
                      {explanation && <p><MathText text={explanation} /></p>}
                      {correctOptionId != null && (
                        <button
                          type="button"
                          className="set-correct-btn"
                          disabled={isUpdating}
                          onClick={() => setEditingExplanation({
                            questionId: qId,
                            text: getOptionExplanation(question, options, correctOptionId)
                          })}
                        >
                          {explanation ? 'Edit explanation' : 'Add explanation'}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="review-question-footer">
                    {tags.length > 0 && (
                      <div className="question-tags">
                        {tags.map((tag) => (
                          <span key={tag} className="question-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    {question.id && (
                      <small className="muted" style={{ marginLeft: 'auto' }}>ID: {question.id}</small>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {confirmUnpublish && (
        <ConfirmDialog
          title="Unpublish question bank?"
          message="Unpublishing will return this question bank to Draft status and deactivate its questions from active quizzes and screeners. You can republish anytime."
          confirmLabel="Unpublish"
          busy={publishing}
          onConfirm={handleUnpublish}
          onCancel={() => setConfirmUnpublish(false)}
        />
      )}

      {unreviewedCount != null && (
        <ConfirmDialog
          title="Publish unreviewed answers?"
          message={`${unreviewedCount} ${unreviewedCount === 1 ? 'question still has an AI-guessed answer' : 'questions still have AI-guessed answers'} nobody has reviewed. Publish anyway?`}
          confirmLabel="Publish anyway"
          busy={publishing}
          onConfirm={() => handlePublish({ force: true })}
          onCancel={() => setUnreviewedCount(null)}
        />
      )}
    </>
  );
}
